import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import z from 'zod';

import { CostingStrategy } from '@invenco/shipping-interface';

import { DialogBody, DialogHeader, DialogTitle } from '@/components/core/dialog';
import { SelectItem } from '@/components/core/select';
import { FormDialogContent } from '@/components/forms/form-dialog';
import { SelectFormField } from '@/components/forms/select-form-field';
import { TextFormField } from '@/components/forms/text-form-field';
import { useGateways } from '@/gateways/GatewayProvider';
import { fetchAllPages } from '@/shared/helpers';
import type { Result } from '@/shared/helpers/Result';
import { costingStrategyTitle } from '@/shared/title-maps';

const schema = z.object({
  carrierId: z.string().min(1, 'Select a carrier'),
  accountId: z.string().min(1, 'Select an account'),
  name: z.string().min(1, 'Enter a name'),
  code: z.string().min(1, 'Enter a code'),
  costingStrategy: z.enum(CostingStrategy),
});

export type CreateCarrierAccountInput = z.infer<typeof schema>;

type Props = {
  onSubmit: (data: CreateCarrierAccountInput) => Promise<Result> | Promise<void> | void;
};

/**
 * Create-carrier-account form, rendered inside a `FormDialog` from the list page. Captures only the
 * account identity — weight brackets are configured afterwards on the detail page.
 */
export function CreateCarrierAccountDialogContent({ onSubmit }: Props) {
  const { shippingGateway, accountsGateway } = useGateways();

  const { data: carriers = [], isLoading: isLoadingCarriers } = useQuery({
    queryKey: ['carriers'],
    queryFn: ({ signal }) =>
      fetchAllPages(({ cursor, take }) =>
        shippingGateway.getCarriers({ cursor, take }, { signal }),
      ),
  });

  const { data: accounts = [], isLoading: isLoadingAccounts } = useQuery({
    queryKey: ['accounts'],
    queryFn: ({ signal }) =>
      fetchAllPages(({ cursor, take }) =>
        accountsGateway.getAccounts({ cursor, take }, { signal }),
      ),
  });

  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      carrierId: '',
      accountId: '',
      name: '',
      code: '',
      costingStrategy: CostingStrategy.QUOTE,
    },
  });

  return (
    <FormDialogContent form={form} submitLabel="Create" onSubmit={onSubmit}>
      <DialogHeader>
        <DialogTitle>New carrier account</DialogTitle>
      </DialogHeader>
      <DialogBody className="grid gap-4 @md:grid-cols-2">
        <SelectFormField
          control={form.control}
          name="carrierId"
          label="Carrier"
          placeholder="Select a carrier"
          className="w-full"
          disabled={isLoadingCarriers}
        >
          {carriers.map((carrier) => (
            <SelectItem key={carrier.id} value={carrier.id}>
              {carrier.name}
            </SelectItem>
          ))}
        </SelectFormField>
        <SelectFormField
          control={form.control}
          name="accountId"
          label="Account"
          placeholder="Select an account"
          className="w-full"
          disabled={isLoadingAccounts}
        >
          {accounts.map((account) => (
            <SelectItem key={account.id} value={account.id}>
              {account.name}
            </SelectItem>
          ))}
        </SelectFormField>
        <TextFormField control={form.control} name="name" label="Name" placeholder="Enter a name" />
        <TextFormField control={form.control} name="code" label="Code" placeholder="Enter a code" />
        <SelectFormField
          control={form.control}
          name="costingStrategy"
          label="Costing strategy"
          className="w-full"
        >
          {Object.values(CostingStrategy).map((strategy) => (
            <SelectItem key={strategy} value={strategy}>
              {costingStrategyTitle[strategy]}
            </SelectItem>
          ))}
        </SelectFormField>
      </DialogBody>
    </FormDialogContent>
  );
}
